/** @jsxImportSource @emotion/react */

import { useMutation, useQuery } from '@apollo/client'
import { createSearchParams, useParams, useSearchParams } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { Typography } from '@mui/material'
import { BLOG_POST_MEDIA_PATH_S3 } from 'constants/images'
import { getTableOfContent, validateTableOfContent } from 'components/TableOfContent'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { useImageUpload } from 'utils/saveImageS3'
import { PageLoader } from 'components/PageLoader'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { ImageField } from 'components/DropzoneField'
import * as classes from 'pagesNew/SuccessStory/Create/styles'
import { FormValues } from 'pagesNew/SuccessStory/Create/types'
import { BlogPostType, Form, VALIDATE_TABLE_OF_CONTENT_OPTIONS } from 'pagesNew/SuccessStory/Create/Form'
import { handleServerErrors, prepareHtmlForSubmit } from 'pagesNew/SuccessStory/Create'
import { DELETE_STUDY_GUIDE, FIND_ONE_STUDY_GUIDE, UPDATE_STUDY_GUIDE } from 'queries/studyGuide'
import { findOneStudyGuide, findOneStudyGuideVariables } from '__generatedTypes__/findOneStudyGuide'
import { updateStudyGuide, updateStudyGuideVariables } from '__generatedTypes__/updateStudyGuide'
import { removeStudyGuide, removeStudyGuideVariables } from '__generatedTypes__/removeStudyGuide'
import { QUERY_PARAM, STUDY_GUIDE_TAB } from 'pagesNew/BlogPages'
import { useFiles } from 'pagesNew/SuccessStory/Edit/hooks'
import { toast } from 'utils/toast'
import { ITEMS } from 'pagesNew/SuccessStory/Create/IconBlock'
import { useGetSimilarPostsInitialValue } from '../Create/hooks'

type Props = {
  platform: Platform
}

export const Edit = withPlatformCheck(({ platform }: Props) => {
  const { studyGuideId } = useParams()
  const [searchParams] = useSearchParams()
  const [isPlatformMatch, setIsPlatformMatch] = useState(true)

  const sectionId = searchParams.get('sectionId')

  const uploadImage = useImageUpload()

  const { navigate } = useLink()

  const { loading: studyGuidesLoading, getInitialValue } = useGetSimilarPostsInitialValue(platform.id, true)

  const { fetchFromLinks, coverFile, isLoading: isLoadingImages } = useFiles()

  const { data, loading } = useQuery<findOneStudyGuide, findOneStudyGuideVariables>(FIND_ONE_STUDY_GUIDE, {
    variables: {
      id: studyGuideId || '',
    },
    onCompleted: async (data) => {
      const studyGuide = data.findOneStudyGuide

      setIsPlatformMatch(studyGuide?.platform.id === platform.id)

      const coverLink = studyGuide?.getCoverImage || ''
      const initialCoverLink = studyGuide?.coverImage

      await fetchFromLinks(
        {
          imageLink: '',
        },
        {
          imageLink: coverLink,
          initialLink: initialCoverLink,
        }
      )
    },
    skip: !studyGuideId,
  })

  const [updateStudyGuide] = useMutation<updateStudyGuide, updateStudyGuideVariables>(UPDATE_STUDY_GUIDE)
  const [removeStudyGuide, { loading: isDeleting }] = useMutation<removeStudyGuide, removeStudyGuideVariables>(
    DELETE_STUDY_GUIDE
  )

  const initialValues: FormValues | null = useMemo(() => {
    const studyGuide = data?.findOneStudyGuide

    if (!studyGuide || isLoadingImages || studyGuidesLoading) {
      return null
    }

    return {
      author: [studyGuide.author],
      title: studyGuide.title,
      contentAsHtml: studyGuide.guideAsHtml,
      withWhiteImageBG: studyGuide.withWhiteImageBG,
      featuredArticleDescription: studyGuide.featuredArticleDescription,
      description: studyGuide.description,
      slug: studyGuide.slug,
      coverImage: coverFile && [coverFile],
      tableOfContent: studyGuide.tableOfContent,
      similarPosts: getInitialValue(
        studyGuide.exploreSimilarGuides.map((studyGuide) => ({
          id: studyGuide.id,
          title: studyGuide.title,
        }))
      ),
      icon: studyGuide.icon ? ITEMS.find(({ id }) => id === studyGuide.icon) : undefined,
    }
  }, [loading, isLoadingImages, studyGuidesLoading])

  const uploadFile = async (coverImage: ImageField | undefined) => {
    const coverImageFile = coverImage?.isUploaded ? null : coverImage?.file

    const coverImagePath = await (coverImageFile
      ? uploadImage(coverImageFile, BLOG_POST_MEDIA_PATH_S3)
      : Promise.resolve(coverImage?.initialValue))

    return coverImagePath
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const { title, description = '', featuredArticleDescription, slug, withWhiteImageBG = false } = values
      const tableOfContent = getTableOfContent(values.contentAsHtml)
      const { error } = validateTableOfContent(tableOfContent, VALIDATE_TABLE_OF_CONTENT_OPTIONS)

      if (error) {
        return
      }

      const guideAsHtml = await prepareHtmlForSubmit(values)

      const coverImage = await uploadFile(values.coverImage?.[0])

      const authorId = values.author[0].id || ''

      await updateStudyGuide({
        variables: {
          updateStudyGuideInputFieldName: {
            id: studyGuideId,
            title,
            description,
            featuredArticleDescription,
            slug,
            author: {
              id: authorId,
            },
            guideAsHtml,
            withWhiteImageBG,
            coverImage,
            tableOfContent,
            platform: {
              id: platform.id,
            },
            similarGuides: values.similarPosts?.map(({ id }) => id),
            icon: values.icon?.id,
          },
        },
      })

      toast.success('Study guide updated successfully')

      if (sectionId) {
        navigate(cmsRoutes.ORGANIZE_EDIT_STUDY_GUIDES, {
          id: sectionId,
        })

        return
      }

      navigate(cmsRoutes.BLOG_PAGES, {}, { searchParams: createSearchParams({ [QUERY_PARAM]: STUDY_GUIDE_TAB.id }) })
    } catch (error) {
      toast.error('Failed to update study guide')
      return handleServerErrors(error)
    }
  }

  const handleDelete = async () => {
    try {
      await removeStudyGuide({
        variables: {
          removeStudyGuideId: studyGuideId || '',
        },
      })

      if (sectionId) {
        navigate(cmsRoutes.ORGANIZE_EDIT_STUDY_GUIDES, {
          id: sectionId,
        })

        return
      }

      navigate(cmsRoutes.BLOG_PAGES, {}, { searchParams: createSearchParams({ [QUERY_PARAM]: STUDY_GUIDE_TAB.id }) })
      toast.success('Study guide deleted successfully')
    } catch (error) {
      toast.error('Failed to delete study guide')
    }
  }

  if (loading || studyGuidesLoading || isLoadingImages) {
    return <PageLoader />
  }

  if (!isPlatformMatch) {
    return (
      <Typography>
        You are trying to edit study guide with different platform (platform in url: {platform.title}, success story
        platform: {data?.findOneStudyGuide?.platform?.title})
      </Typography>
    )
  }

  if (!initialValues) {
    return <Typography>Something went wrong</Typography>
  }

  const isPostUsedInSection = data?.findOneStudyGuide?.isUsedInSection

  return (
    <section css={classes.container}>
      <Form
        isEditing
        initialValues={initialValues}
        blogPostType={BlogPostType.StudyGuide}
        platform={platform}
        entityId={studyGuideId}
        onSubmit={handleSubmit}
        onDelete={!isPostUsedInSection ? handleDelete : undefined}
        isDeleting={isDeleting}
      />
    </section>
  )
})
